/**
 * PageSectionRenderer — renders the ordered `sections[]` block model
 * that admins compose in the Page Manager (see `UniversalPageManager.jsx`).
 *
 * Block types supported: `hero`, `text`, `image`, `gallery`, `cta`.
 *
 * Renders NOTHING (returns null) if `sections` is empty or undefined —
 * so callers can safely wrap their default template with it: the default
 * only appears when no admin-authored content exists.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${BACKEND_URL}${url}`;
};

const Section = ({ children, testId }) => (
  <motion.section
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    className="py-12 md:py-16 px-6 md:px-10"
    data-testid={testId}
  >
    {children}
  </motion.section>
);

const HeroBlock = ({ content, i }) => {
  const bg = resolveImg(content?.backgroundImage);
  return (
    <section
      className="relative py-24 md:py-32 px-6 md:px-10 overflow-hidden"
      data-testid={`ps-hero-${i}`}
      style={
        bg
          ? {
              backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url(${bg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              color: '#fff',
            }
          : undefined
      }
    >
      <div className="max-w-5xl mx-auto text-center">
        {content?.title && (
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-4">
            {content.title}
          </h1>
        )}
        {content?.subtitle && (
          <p className={`text-base md:text-lg ${bg ? 'text-white/90' : 'text-gray-600'} max-w-3xl mx-auto`}>
            {content.subtitle}
          </p>
        )}
        {content?.ctaText && content?.ctaLink && (
          <div className="mt-8">
            <Link
              to={content.ctaLink}
              className="inline-block px-8 py-3 bg-black text-white uppercase tracking-widest text-xs hover:bg-gray-800 transition-colors"
              data-testid={`ps-hero-cta-${i}`}
            >
              {content.ctaText}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

const TextBlock = ({ content, i }) => {
  const align = content?.alignment === 'center' ? 'text-center'
              : content?.alignment === 'right'  ? 'text-right'
              : 'text-left';
  const body = (content?.body || '').split('\n').filter(Boolean);
  return (
    <Section testId={`ps-text-${i}`}>
      <div className={`max-w-3xl mx-auto ${align}`}>
        {content?.heading && (
          <h2 className="text-2xl md:text-3xl font-light mb-6">{content.heading}</h2>
        )}
        {body.map((p, k) => (
          <p key={k} className="text-base leading-relaxed text-gray-700 mb-4">{p}</p>
        ))}
      </div>
    </Section>
  );
};

const ImageBlock = ({ content, i }) => {
  const src = resolveImg(content?.imageUrl);
  if (!src) return null;
  return (
    <Section testId={`ps-image-${i}`}>
      <figure className="max-w-5xl mx-auto">
        <img
          src={src}
          alt={content?.alt || content?.caption || 'Image'}
          className="w-full h-auto"
          loading="lazy"
        />
        {content?.caption && (
          <figcaption className="mt-3 text-sm text-gray-500 italic text-center">
            {content.caption}
          </figcaption>
        )}
      </figure>
    </Section>
  );
};

const GalleryBlock = ({ content, i }) => {
  const images = content?.images || [];
  if (images.length === 0) return null;
  return (
    <Section testId={`ps-gallery-${i}`}>
      <div className="max-w-6xl mx-auto">
        {content?.title && (
          <h2 className="text-2xl md:text-3xl font-light mb-8 text-center">{content.title}</h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((img, k) => {
            const src = typeof img === 'string' ? img : img?.url;
            const caption = typeof img === 'object' ? img?.caption : null;
            if (!src) return null;
            return (
              <figure key={k}>
                <img
                  src={resolveImg(src)}
                  alt={caption || `Gallery image ${k + 1}`}
                  className="w-full aspect-square object-cover"
                  loading="lazy"
                />
                {caption && (
                  <figcaption className="mt-2 text-xs text-gray-500 italic">{caption}</figcaption>
                )}
              </figure>
            );
          })}
        </div>
      </div>
    </Section>
  );
};

const CtaBlock = ({ content, i }) => (
  <Section testId={`ps-cta-${i}`}>
    <div className="max-w-3xl mx-auto text-center bg-gray-50 border border-gray-100 p-10 md:p-14">
      {content?.title && (
        <h2 className="text-2xl md:text-3xl font-light mb-3">{content.title}</h2>
      )}
      {content?.description && (
        <p className="text-base text-gray-600 mb-6">{content.description}</p>
      )}
      {content?.buttonText && content?.buttonLink && (
        <Link
          to={content.buttonLink}
          className="inline-block px-8 py-3 bg-black text-white uppercase tracking-widest text-xs hover:bg-gray-800 transition-colors"
          data-testid={`ps-cta-btn-${i}`}
        >
          {content.buttonText}
        </Link>
      )}
    </div>
  </Section>
);

const BLOCKS = {
  hero: HeroBlock,
  text: TextBlock,
  image: ImageBlock,
  gallery: GalleryBlock,
  cta: CtaBlock,
};

const PageSectionRenderer = ({ sections }) => {
  if (!Array.isArray(sections) || sections.length === 0) return null;
  const ordered = [...sections]
    .filter((s) => s?.visible !== false)
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  return (
    <div data-testid="page-sections">
      {ordered.map((section, i) => {
        const Block = BLOCKS[section.type];
        if (!Block) return null;
        return <Block key={section.id || i} content={section.content} i={i} />;
      })}
    </div>
  );
};

export default PageSectionRenderer;
